<template>
  <div id="ChangeRouteLocality" class="support-view">
    <Title
      title="Cambiar localidad de ordenes"
      subtitle="Carga SOC, revisa direccion y localidad actual, y aplica una nueva localidad."
    />

    <div class="content">
      <section class="section-block">
        <div class="section-header">
          <span class="section-step">1</span>
          <span class="section-title">CARGAR ORDENES</span>
        </div>

        <div class="form-field orders-input-wrapper">
          <label class="field-label" for="orders-input">SOC</label>
          <el-input
            id="orders-input"
            ref="ordersInput"
            v-model="ordersText"
            type="textarea"
            :rows="7"
            resize="none"
            placeholder="Una o varias SOC separadas por salto de linea, coma o espacio"
          />
        </div>

        <div class="actions-row">
          <el-button
            type="success"
            class="action-button"
            :disabled="!hasSearchInput"
            :loading="loadingOrders"
            @click="loadOrders"
          >
            <i class="el-icon-search" />
            CARGAR ORDENES
          </el-button>

          <el-button
            class="secondary-button"
            :disabled="loadingOrders || saving"
            @click="clearForm"
          >
            LIMPIAR
          </el-button>
        </div>
      </section>

      <section
        v-if="orders.length > 0"
        class="section-block"
      >
        <div class="orders-card">
          <div class="section-header table-header">
            <span class="section-title">ORDENES ENCONTRADAS</span>
            <span class="counter">{{ orders.length }} ordenes</span>
          </div>

          <div class="summary-grid">
            <div class="summary-item">
              <span class="summary-label">Localidades</span>
              <span class="summary-value">{{ localitySummary }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Rutas asociadas</span>
              <span class="summary-value">{{ routeSummary }}</span>
            </div>
          </div>

          <el-table
            class="orders-table"
            :data="orders"
            border
            height="320"
          >
            <el-table-column
              prop="suborder"
              label="SOC"
              width="150"
            />
            <el-table-column
              prop="current_do"
              label="DO"
              width="140"
            />
            <el-table-column
              prop="raw_address"
              label="Direccion"
              min-width="260"
            >
              <template slot-scope="scope">
                {{ formatRawAddress(scope.row.raw_address) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="locality_identifier"
              label="Identifier localidad"
              min-width="180"
            />
            <el-table-column
              prop="locality"
              label="Localidad actual"
              min-width="180"
            />
            <el-table-column
              label="Nueva localidad"
              min-width="240"
            >
              <template slot-scope="scope">
                <el-select
                  v-model="scope.row.new_locality_id"
                  class="full-control"
                  filterable
                  clearable
                  :loading="loadingLocalities"
                  placeholder="Seleccionar"
                >
                  <el-option
                    v-for="locality in localityOptions"
                    :key="locality.id"
                    :label="locality.name"
                    :value="locality.id"
                  />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column
              prop="last_state_name"
              label="Estado"
              min-width="140"
            />
          </el-table>
        </div>
      </section>

      <section
        v-if="orders.length > 0"
        class="section-block"
      >
        <div class="section-header">
          <span class="section-step">2</span>
          <span class="section-title">CAMBIO DE LOCALIDAD</span>
        </div>

        <div class="form-grid">
          <div class="form-field">
            <label class="field-label" for="locality-reason">Motivo</label>
            <el-input
              id="locality-reason"
              v-model="reason"
              class="full-control"
              placeholder="Ej: correccion operacional"
            />
          </div>
        </div>

        <el-alert
          v-if="previewLocality"
          class="support-alert"
          type="info"
          :closable="false"
          :title="previewLocality"
        />

        <el-button
          type="success"
          class="action-button"
          :disabled="!canSave"
          :loading="saving"
          @click="saveLocalityChange"
        >
          GUARDAR CAMBIO DE LOCALIDAD
        </el-button>
      </section>
    </div>
  </div>
</template>

<script>
import { apiRequest } from '@/backend/vue-axios/axios';
import { decodeRSA } from '@/mixins/function';
import Title from '../components/base/Title.vue';

export default {
  name: 'ChangeRouteLocality',
  components: {
    Title,
  },
  data() {
    return {
      ordersText: '',
      orders: [],
      reason: '',
      loadingOrders: false,
      loadingLocalities: false,
      saving: false,
      localityOptions: [],
    };
  },
  computed: {
    suborders() {
      return this.parseNumericList(this.ordersText);
    },
    hasSearchInput() {
      return this.suborders.length > 0;
    },
    canSave() {
      return this.localityUpdates.length > 0;
    },
    previewLocality() {
      if (this.localityUpdates.length === 0) return '';
      return `${this.localityUpdates.length} ordenes tienen una nueva localidad seleccionada.`;
    },
    localityUpdates() {
      return this.orders
        .filter((order) => {
          const targetLocalityID = Number(order.new_locality_id);
          const currentLocalityID = Number(order.locality_id);
          return Number.isInteger(targetLocalityID)
            && targetLocalityID > 0
            && targetLocalityID !== currentLocalityID;
        })
        .map(order => ({
          suborder: Number(order.suborder),
          locality_id: Number(order.new_locality_id),
        }));
    },
    localitySummary() {
      const localities = this.uniqueValues(this.orders.map(order => (
        order.locality_identifier || order.locality
      )));
      return localities.length > 0 ? localities.join(', ') : 'Sin localidad';
    },
    routeSummary() {
      const routeIDs = this.uniqueValues(this.orders
        .map(order => order.route_id)
        .filter(routeID => routeID && routeID > 0));
      return routeIDs.length > 0 ? routeIDs.join(', ') : 'Sin ruta asociada';
    },
  },
  mounted() {
    this.$refs.ordersInput.focus();
    this.loadLocalities();
  },
  methods: {
    parseTextList(value) {
      return value
        .split(/[\s,;]+/)
        .map(item => item.trim())
        .filter(item => item !== '')
        .filter((item, index, list) => list.indexOf(item) === index);
    },
    parseNumericList(value) {
      return this.parseTextList(value)
        .map(item => Number(item))
        .filter(item => Number.isInteger(item) && item > 0)
        .filter((item, index, list) => list.indexOf(item) === index);
    },
    uniqueValues(values) {
      return values
        .filter(value => value !== undefined && value !== null && value !== '')
        .filter((value, index, list) => list.indexOf(value) === index);
    },
    normalizeOrders(responseData) {
      if (Array.isArray(responseData)) {
        return responseData;
      }
      return responseData ? [responseData] : [];
    },
    formatRawAddress(rawAddress) {
      if (!rawAddress) return '';
      try {
        return decodeRSA(rawAddress);
      } catch (error) {
        return rawAddress;
      }
    },
    loadLocalities() {
      this.loadingLocalities = true;

      apiRequest({
        module: 'crud',
        service: 'get_localities',
        params: {
          limit: 100000,
          offset: 0,
        },
      }).then((response) => {
        this.localityOptions = this.normalizeOrders(response.data)
          .filter(locality => locality.id && locality.name)
          .sort((a, b) => a.name.localeCompare(b.name));
      }).catch(() => {
        this.$notify({
          type: 'error',
          title: 'Error',
          message: 'No se pudo cargar la lista de localidades.',
        });
      }).finally(() => {
        this.loadingLocalities = false;
      });
    },
    loadOrders() {
      if (!this.hasSearchInput) {
        this.$message.warning('Ingresa al menos una SOC valida.');
        return;
      }

      this.loadingOrders = true;
      this.orders = [];

      apiRequest({
        module: 'planning',
        service: 'get_route_locality_orders',
        params: {
          suborders: this.suborders,
        },
      })
        .then((response) => {
          this.orders = this.normalizeOrders(response.data).map(order => ({
            ...order,
            new_locality_id: '',
          }));

          if (this.orders.length === 0) {
            this.$message.warning('No se encontraron ordenes para los datos ingresados.');
          }
        })
        .catch((error) => {
          const message = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'No fue posible cargar las ordenes.';

          this.$message.error(message);
        })
        .finally(() => {
          this.loadingOrders = false;
        });
    },
    saveLocalityChange() {
      if (!this.canSave) {
        this.$message.warning('Selecciona al menos una nueva localidad.');
        return;
      }

      this.saving = true;

      apiRequest({
        module: 'planning',
        service: 'change_route_locality',
        params: {
          updates: this.localityUpdates,
        },
      }).then((response) => {
        const updatedSuborders = response.data && response.data.updated_suborders
          ? response.data.updated_suborders
          : this.localityUpdates.length;

        this.orders = this.orders.map((order) => {
          const targetLocalityID = Number(order.new_locality_id);
          const currentLocalityID = Number(order.locality_id);
          if (!targetLocalityID || targetLocalityID === currentLocalityID) {
            return order;
          }

          const locality = this.localityOptions.find(item => Number(item.id) === targetLocalityID);
          return {
            ...order,
            locality_id: targetLocalityID,
            locality: locality ? locality.name : order.locality,
            locality_identifier: locality ? locality.political_area_id : order.locality_identifier,
            new_locality_id: '',
          };
        });
        this.$message({
          type: 'success',
          message: `Localidad actualizada para ${updatedSuborders} ordenes.`,
        });
      }).catch((error) => {
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'No fue posible cambiar la localidad.';

        this.$message.error(message);
      }).finally(() => {
        this.saving = false;
      });
    },
    clearForm() {
      this.ordersText = '';
      this.orders = [];
      this.reason = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.support-view {
  color: $fb-grey-dark-50;
}

.section-block {
  margin-bottom: 22px;
  padding-top: 6px;
  border-top: 1px solid $fb-grey-light-84;
}

.section-header,
.actions-row {
  display: flex;
  align-items: center;
}

.section-header {
  margin-bottom: 14px;
}

.table-header {
  justify-content: space-between;
  max-width: 980px;
}

.section-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 8px;
  border: 2px solid $fb-green;
  border-radius: 50%;
  color: $fb-green;
  font-size: 12px;
  font-weight: 700;
}

.section-title,
.field-label {
  color: $fb-grey-dark-50;
  font-weight: 700;
}

.section-title,
.field-label {
  font-size: 14px;
}

.form-grid {
  display: grid;
  gap: 16px;
  margin-bottom: 18px;
}

.orders-input-wrapper {
  max-width: 650px;
  margin-bottom: 18px;
}

.form-grid {
  grid-template-columns: repeat(2, minmax(220px, 360px));
}

.field-label {
  display: block;
  margin: 0 0 8px;
}

.full-control {
  width: 100%;
}

.action-button {
  min-width: 210px;
  font-weight: 700;
}

.secondary-button {
  margin-left: 10px;
  font-weight: 700;
}

.orders-table {
  margin-bottom: 4px;
}

.orders-card {
  max-width: 1080px;
  padding: 16px;
  border: 1px solid $fb-grey-light-84;
  border-radius: 6px;
  background-color: white;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.summary-item {
  min-height: 52px;
  padding: 10px 12px;
  border: 1px solid $fb-grey-light-92;
  background-color: #f8f9fa;
}

.summary-label,
.summary-value {
  display: block;
}

.summary-label {
  margin-bottom: 4px;
  color: $fb-grey-dark-50;
  font-size: 12px;
  font-weight: 700;
}

.summary-value {
  color: $fb-grey-dark-50;
  font-size: 14px;
}

.counter {
  color: $fb-grey-dark-50;
  font-size: 13px;
}

.support-alert {
  max-width: 760px;
  margin-bottom: 18px;
}

::v-deep .action-button.el-button--success {
  border-color: $fb-green;
  background-color: $fb-green;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .actions-row {
    display: block;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .action-button,
  .secondary-button {
    width: 100%;
    min-width: 0;
    margin: 0 0 12px;
  }
}
</style>
